import React, { useEffect, useState } from 'react';
import { t } from 'i18next';
import BahlouLogoFull from '@/assets/Bahlou_logo.png';
import EyeIcon from '@/assets/eye-icon.svg';
import EyeOffIcon from '@/assets/eye-off-icon.svg';
import { RadixButton, RadixCard, RadixInput, RadixOtpInput, RadixSpinner } from '@/components/radix';
import { FormikProps, useFormik } from 'formik';
import * as Yup from 'yup';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { completeAdminSetupApi, validateOutletSetupTokenApi } from '@/utils/Api/AuthV3';
import { SetupValidateTokenData, SetupValidateTokenResponse } from '@/utils/Api/AuthV3/authV3.types';
import { EmailOutlined } from '@mui/icons-material';
import { toast } from 'sonner';

const MIN_PASSWORD_LENGTH = 6;
type StoreSetupFormValues = {
    email: string;
    employeeName: string;
    newPassword: string;
    confirmPassword: string;
    adminAccessCode: string;
    showNew: boolean;
    showConfirm: boolean;
};

export default function StoreSetupIndex() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [isValidatingToken, setIsValidatingToken] = useState(true);
    const [currentStep, setCurrentStep] = useState<1 | 2>(1);
    const [apiResponse, setApiResponse] = useState<SetupValidateTokenData | null>(null);

    const [formValues, setFormValues] = useState<StoreSetupFormValues>({
        email: '',
        employeeName: '',
        newPassword: '',
        confirmPassword: '',
        adminAccessCode: '',
        showNew: false,
        showConfirm: false,
    });

    const validationSchema = Yup.object().shape({
        employeeName: Yup.string().required(t('Setting.PleaseEnterName')),
        newPassword: Yup.string()
            .min(MIN_PASSWORD_LENGTH, t('Setting.StoreInvitePasswordMinLength'))
            .required(t('Setting.StoreInvitePasswordRequired')),
        confirmPassword: Yup.string()
            .oneOf([Yup.ref('newPassword')], t('Setting.StoreInvitePasswordsMustMatch'))
            .required(t('Setting.StoreInviteConfirmRequired')),
        adminAccessCode: Yup.string().matches(/^\d{6}$/, {
            message: t('Setting.StoreInviteAdminAccessCodeInvalid'),
            excludeEmptyString: true,
        }),
    });

    useEffect(() => {
        // clear storage when component unmounts
        localStorage.clear();
    }, []);

    const formik = useFormik({
        initialValues: formValues,
        enableReinitialize: true,
        validationSchema,
        onSubmit: async (values) => {
            const token = searchParams.get('token');
            if (!token) {
                navigate('/', { replace: true });
                return;
            }

            try {
                await completeAdminSetupApi({
                    token,
                    password: values.newPassword,
                    password_confirm: values.confirmPassword,
                    employee_name: values.employeeName.trim(),
                    login_email: values.email.trim(),
                    admin_access_code: values.adminAccessCode || undefined,
                });
                toast.success('Store setup completed successfully');
                setIsValidatingToken(false);
                formik.resetForm();
                navigate('/', { replace: true });
            } catch (error) {
                // Keep form values for retry if API fails.
                toast.error('Invalid token or token has expired');
                setIsValidatingToken(false);
                formik.resetForm();
                navigate('/', { replace: true });
            }
        },
    });

    const handleNextStep = async () => {
        const errors = await formik.validateForm();
        const hasPasswordErrors = Boolean(errors.newPassword || errors.confirmPassword);
        if (hasPasswordErrors) {
            formik.setTouched(
                {
                    ...formik.touched,
                    newPassword: true,
                    confirmPassword: true,
                },
                true,
            );
            return;
        }
        setCurrentStep(2);
    };

    useEffect(() => {
        const token = searchParams.get('token');
        if (!token) {
            navigate('/', { replace: true });
            return;
        }

        const validateToken = async () => {
            try {
                const response: SetupValidateTokenResponse = await validateOutletSetupTokenApi(token);
                const data = response?.data;
                if (!data?.valid) {
                    toast.error('Invalid token or token has expired');
                    setIsValidatingToken(false);
                    formik.resetForm();
                    navigate('/', { replace: true });
                    return;
                }
                setApiResponse(data);
                const email = data?.store_details?.email || '';
                const employeeName = data?.employee_name || data?.employee?.name || '';
                setFormValues((prev) => ({ ...prev, email, employeeName }));
            } catch (error) {
                toast.error('Invalid token or token has expired');
                setIsValidatingToken(false);
                formik.resetForm();
                navigate('/', { replace: true });
            } finally {
                setIsValidatingToken(false);
            }
        };
        validateToken();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [navigate, searchParams]);

    if (isValidatingToken) {
        return (
            <div className="h-screen w-full flex items-center justify-center bg-[#FDFCFB]">
                <RadixSpinner size="lg" />
            </div>
        );
    }

    return (
        <div className="relative min-h-screen sm:h-screen w-full flex flex-col items-center justify-center bg-[#FDFCFB] overflow-hidden px-4 py-6">
            {/* Background Decorations - Hidden on small mobile to keep it light */}
            <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-primary-500/10 blur-3xl hidden sm:block" />
            <div className="absolute -bottom-24 -left-24 w-72 h-72 rounded-full bg-primary-500/10 blur-3xl hidden sm:block" />

            <div className="z-10 w-full max-w-[480px] flex flex-col justify-center">
                {/* Responsive Logo */}
                <div className="flex justify-center mb-6 sm:mb-10">
                    <img src={BahlouLogoFull} alt="Bahlou" className="h-8 sm:h-12 object-contain" />
                </div>

                <RadixCard className="!p-0 border !border-[#E6E1D9] shadow-xl !rounded-md bg-white/95 backdrop-blur-md overflow-hidden">
                    {/* Compact Header for Mobile */}
                    <div className="bg-white p-5 sm:p-8 text-center border-b border-[#E6E1D9]">
                        <div className="relative inline-block">
                            <div className="w-14 h-14 sm:w-20 sm:h-20 bg-primary-500 text-white rounded-full flex items-center justify-center text-xl sm:text-3xl font-bold mx-auto mb-2 sm:mb-4 shadow-lg border-2 sm:border-4 border-white">
                                {apiResponse?.outlet_name?.charAt(0) || 'B'}
                            </div>
                        </div>
                        <h2 className="m-0 text-lg sm:text-2xl font-bold text-gray-800 tracking-tight">
                            {t('Setting.StoreInviteFlowTitle')}
                        </h2>
                    </div>

                    <div className="p-4 md:p-8">
                        <p className="text-gray-500 text-[11px] sm:text-sm text-center mb-6 sm:mb-8 leading-relaxed">
                            {t('Setting.StoreInvitePasswordDescription', { storeName: apiResponse?.outlet_name })}
                        </p>

                        <div className="space-y-4 md:space-y-5">
                            <Stepper currentStep={currentStep} />
                            {currentStep === 1 ? (
                                <PasswordSection formik={formik} />
                            ) : (
                                <AdminDetailsSection formik={formik} />
                            )}

                            {currentStep === 1 ? (
                                <RadixButton
                                    variant="primary"
                                    className="w-full h-10 !rounded-md !text-sm font-bold shadow-lg shadow-primary-500/20 mt-2 sm:mt-4 text-white"
                                    onClick={handleNextStep}
                                >
                                    {t('Common.Next')}
                                </RadixButton>
                            ) : (
                                <div className="grid grid-cols-2 gap-3 mt-2 sm:mt-4">
                                    <RadixButton
                                        variant="outline"
                                        className="h-10 !rounded-md !text-sm font-bold"
                                        onClick={() => setCurrentStep(1)}
                                        disabled={formik.isSubmitting}
                                    >
                                        {t('Common.Back')}
                                    </RadixButton>
                                    <RadixButton
                                        variant="primary"
                                        className="h-10 !rounded-md !text-sm font-bold shadow-lg shadow-primary-500/20 text-white"
                                        onClick={() => formik.handleSubmit()}
                                        disabled={formik.isSubmitting}
                                    >
                                        {formik.isSubmitting ? <RadixSpinner size="sm" /> : t('Common.CompleteSetup')}
                                    </RadixButton>
                                </div>
                            )}
                        </div>
                    </div>
                </RadixCard>
            </div>
        </div>
    );
}

const sectionTitleClass = 'text-xs sm:text-sm font-semibold text-gray-700 mb-3 uppercase tracking-wide';
const fieldLabelClass = 'text-[10px] md:text-base font-bold text-gray-400 tracking-widest ml-1';

function Stepper({ currentStep }: { currentStep: 1 | 2 }) {
    return (
        <div className="mb-1">
            <div className="flex items-center gap-2">
                <div className={`h-2 flex-1 rounded-full ${currentStep >= 1 ? 'bg-primary-500' : 'bg-gray-200'}`} />
                <div className={`h-2 flex-1 rounded-full ${currentStep >= 2 ? 'bg-primary-500' : 'bg-gray-200'}`} />
            </div>
            <p className="text-[11px] sm:text-xs text-gray-500 mt-2 text-center">
                {currentStep === 1
                    ? t('Setting.StoreInvitePasswordSectionTitle')
                    : t('Setting.StoreInviteAdminDetailsSectionTitle')}
            </p>
        </div>
    );
}

function PasswordSection({ formik }: { formik: FormikProps<StoreSetupFormValues> }) {
    return (
        <div className="space-y-3 rounded-md border border-[#E6E1D9] bg-[#FCFAF7] p-3 sm:p-4">
            <h3 className={sectionTitleClass}>{t('Setting.StoreInvitePasswordSectionTitle')}</h3>
            <div className="space-y-1">
                <label className={fieldLabelClass}>{t('Common.Email')}</label>
                <div className="flex items-center gap-2 sm:gap-3 px-3 py-2 sm:px-4 sm:py-3 cursor-not-allowed bg-white border border-gray-100 rounded-md text-text-secondary">
                    <EmailOutlined className="w-4 h-4 text-text-secondary" />
                    <span className="text-xs sm:text-sm truncate">{formik.values.email}</span>
                </div>
            </div>

            <RadixInput
                label={<label className={fieldLabelClass}>{t('Common.NewPassP')}</label>}
                id="newPassword"
                name="newPassword"
                type={formik.values.showNew ? 'text' : 'password'}
                value={formik.values.newPassword}
                onChange={formik.handleChange}
                error={formik.touched.newPassword ? formik.errors.newPassword : undefined}
                className="!rounded-md !bg-white !h-10 sm:!h-12"
                endComponent={
                    <span className="pointer-events-auto">
                        <button
                            type="button"
                            onClick={() => formik.setFieldValue('showNew', !formik.values.showNew)}
                            className="pr-2 text-gray-400 bg-transparent border-none outline-none cursor-pointer"
                        >
                            <img
                                src={formik.values.showNew ? EyeOffIcon : EyeIcon}
                                alt=""
                                aria-hidden="true"
                                className="w-4 h-4"
                            />
                        </button>
                    </span>
                }
            />
            <RadixInput
                label={<label className={fieldLabelClass}>{t('Common.ConfPassp')}</label>}
                id="confirmPassword"
                name="confirmPassword"
                type={formik.values.showConfirm ? 'text' : 'password'}
                value={formik.values.confirmPassword}
                onChange={formik.handleChange}
                error={formik.touched.confirmPassword ? formik.errors.confirmPassword : undefined}
                className="!rounded-md !bg-white !h-10 sm:!h-12"
                endComponent={
                    <span className="pointer-events-auto">
                        <button
                            type="button"
                            onClick={() => formik.setFieldValue('showConfirm', !formik.values.showConfirm)}
                            className="pr-2 text-gray-400 bg-transparent border-none outline-none cursor-pointer"
                        >
                            <img
                                src={formik.values.showConfirm ? EyeOffIcon : EyeIcon}
                                alt=""
                                aria-hidden="true"
                                className="w-4 h-4"
                            />
                        </button>
                    </span>
                }
            />
        </div>
    );
}

function AdminDetailsSection({ formik }: { formik: FormikProps<StoreSetupFormValues> }) {
    return (
        <div className="space-y-3 rounded-md border border-[#E6E1D9] bg-[#FCFAF7] p-3 sm:p-4">
            <h3 className={sectionTitleClass}>{t('Setting.StoreInviteAdminDetailsSectionTitle')}</h3>
            <RadixInput
                label={<label className={fieldLabelClass}>{t('Common.Name')}</label>}
                id="employeeName"
                name="employeeName"
                type="text"
                value={formik.values.employeeName}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.employeeName ? formik.errors.employeeName : undefined}
                className="!rounded-md !bg-white !h-10 sm:!h-12"
            />
            <RadixOtpInput
                label={<label className={fieldLabelClass}>{t('Setting.StoreInviteAdminAccessCodeLabel')}</label>}
                id="adminAccessCode"
                name="adminAccessCode"
                autoComplete="one-time-code"
                length={6}
                value={formik.values.adminAccessCode}
                onChange={(value) => {
                    void formik.setFieldValue('adminAccessCode', value);
                }}
                onBlur={formik.handleBlur}
                error={formik.touched.adminAccessCode ? formik.errors.adminAccessCode : undefined}
                helperText={t('Setting.StoreInviteAdminAccessCodeHelper')}
                inputClassName="!rounded-md !bg-white !h-10 sm:!h-12"
            />
        </div>
    );
}
